import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  catchAndContinue,
  convertEquipmentNumberToEquipmentRunningNumber,
  filterUndefined,
  IFilter,
  ODataOperator,
  SapError,
} from 'src/app/lego-utils';
import isEmpty from 'lodash-es/isEmpty';
import sortBy from 'lodash-es/sortBy';
import { NzModalService } from 'ng-zorro-antd/modal';
import { combineLatest, of, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  BomLockedValidation,
  MCode500Validation,
  MCodeValidationNotPossibleError,
  MultiKVCode510Validation,
  PartDesignAcceptedInTeamcenterValidation,
  Teamcenter2DMCodeValidation,
  TechnicalSpecificationFormatValidation,
  TechnicalSpecificationSequenceValidation,
} from 'src/app/components/header/validation/rules';
import { getBomType, isBaselineRevision, isDuploTopMainGroup } from 'src/app/helpers';
import { taskDesignIdKey, TaskListHeader } from 'src/app/models/domain';
import {
  BomHeaderApiService,
  BOMItemDataService,
  CharacteristicDataService,
  IWithTotal,
  LegoRestApiDataService,
  MaterialDataService,
  SpecificationDataService,
  TaskDataService,
} from 'src/app/services';
import { DesignService, ValidationService } from 'src/app/state';
import { EquipmentLookupService } from 'src/app/state/equipment/equipment-lookup.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { OqcValidationService } from '../../../services/oqc-validation.service';
import { OqcValidation } from './rules/oqc.rule';
import { ValidationSummaryModalComponent } from './validation-summary-modal/validation-summary-modal.component';

@Component({
  selector: 'em-validation',
  templateUrl: './validation.component.html',
  standalone: false,
})
export class ValidationComponent implements OnInit, OnDestroy {
  protected readonly failingRuleCount$ = this.validationService.failingRuleCount$;

  private readonly oqcValidationEnabled$ =
    this.featureFlagService.isFeatureEnabled('oqc_validation_enabled');

  private readonly designId$ = this.designService.selectDesignId().pipe(
    tap(() => {
      this.validationService.reset();
    }),
    filterUndefined(),
    switchMap((designId) =>
      this.oqcValidationEnabled$.pipe(map((oqcEnabled) => ({ designId, oqcEnabled })))
    ),
    tap(({ oqcEnabled }) => {
      const loadingValidations = [
        PartDesignAcceptedInTeamcenterValidation.name,
        MCode500Validation.name,
        TechnicalSpecificationFormatValidation.name,
        TechnicalSpecificationSequenceValidation.name,
        Teamcenter2DMCodeValidation.name,
        MultiKVCode510Validation.name,
        BomLockedValidation.name,
      ];

      if (oqcEnabled) {
        loadingValidations.push(OqcValidation.name);
      }

      this.validationService.setValidationsToLoading(loadingValidations);
    }),
    map(({ designId }) => designId),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  /**
   * @param validations the validations which rely on technical specification data.
   * @return Technical specifications.
   */
  private readonly allTechnicalSpecifications$ = this.designId$.pipe(
    switchMap((designId) =>
      catchAndContinue(
        this.specificationDataService.getAllTechnicalSpecificationsByDesignId(designId),
        (error) => {
          this.handleError(
            [
              PartDesignAcceptedInTeamcenterValidation.name,
              MCode500Validation.name,
              TechnicalSpecificationFormatValidation.name,
              TechnicalSpecificationSequenceValidation.name,
              Teamcenter2DMCodeValidation.name,
              MultiKVCode510Validation.name,
            ],
            error,
            `Unable to find Technical Specification for design id ${designId}`
          );
        }
      )
    ),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly activeEquipmentNumber$ = this.equipmentLookup.selectedEquipmentId$.pipe(
    tap(() => {
      this.validationService.resetValidations([
        MCode500Validation.name,
        TechnicalSpecificationSequenceValidation.name,
        MultiKVCode510Validation.name,
      ]);
    }),
    filterUndefined(),
    tap(() => {
      this.validationService.setValidationsToLoading([
        MCode500Validation.name,
        TechnicalSpecificationSequenceValidation.name,
        MultiKVCode510Validation.name,
      ]);
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  /**
   * @param validations the validations which rely on technical specification for active equipment.
   * @return Technical specifications.
   */
  private readonly technicalSpecificationsForEquipment$ = combineLatest([
    this.activeEquipmentNumber$,
    this.allTechnicalSpecifications$,
  ]).pipe(
    filter((arr): arr is [string, IWithTotal<TaskListHeader>] => !!arr[0] && !!arr[1]),
    map(([equipmentNumber, technicalSpecificationHeaders]) => {
      const equipmentRunningNumber =
        convertEquipmentNumberToEquipmentRunningNumber(equipmentNumber);

      // tsForEquipment
      return technicalSpecificationHeaders.data.filter((taskListHeader) =>
        taskListHeader.description.includes(`-${equipmentRunningNumber}`)
      );
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  private readonly teamcenterPartDesignStructure$ = this.designId$.pipe(
    switchMap((designId) =>
      catchAndContinue(
        this.legoRestApiDataService.getItemStructureById(`PD${designId}`),
        (error) => {
          this.handleError(
            [PartDesignAcceptedInTeamcenterValidation.name, Teamcenter2DMCodeValidation.name],
            error,
            `Unable to find PD${designId} in Teamcenter`
          );
        }
      )
    ),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  private readonly partDesign2DMCodes$ = this.teamcenterPartDesignStructure$.pipe(
    tap(() => {
      this.validationService.setValidationsToLoading(Teamcenter2DMCodeValidation.name);
    }),
    switchMap((partDesign) => {
      if (!partDesign) {
        return of(undefined);
      }

      // Revisions are ordered newest-first (see PartDesignItemStructureAdapter), so the first
      // baseline revision found is the latest baseline. A baseline is a revision without an
      // iteration suffix on its revId (e.g. Q is the baseline of Q, Q.01, Q.02).
      const baselineRevision = partDesign.revisions.find((revision) =>
        isBaselineRevision(revision.revId)
      );

      return baselineRevision
        ? catchAndContinue(
            this.legoRestApiDataService.getMCodes(partDesign.itemId, baselineRevision.revId).pipe(
              map((mCodes) => ({
                mCodes,
                revisionDescription: `${partDesign.itemId}/${baselineRevision.revId}`,
              }))
            ),
            (error) => {
              this.handleError(
                Teamcenter2DMCodeValidation.name,
                error,
                `Unable to find M-codes for ${partDesign.itemId}/${baselineRevision.revId} in Teamcenter`
              );
            }
          )
        : of(undefined);
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  private readonly masterCharacteristicsFromTechnicalSpecificationsForActiveEquipment$ =
    this.technicalSpecificationsForEquipment$.pipe(
      tap(() => {
        this.validationService.setValidationsToLoading([
          MCode500Validation.name,
          TechnicalSpecificationSequenceValidation.name,
          MultiKVCode510Validation.name,
        ]);
      }),
      map((taskListHeaders) =>
        taskListHeaders.filter((taskListHeader) => taskListHeader.description.includes('Master'))
      ),
      switchMap((masterTaskListHeaders) => {
        if (isEmpty(masterTaskListHeaders)) {
          return of([]);
        }

        const taskListGroup = masterTaskListHeaders[0].taskListGroup;
        const masterGroupCounters = masterTaskListHeaders.map(({ groupCounter }) => groupCounter);

        return catchAndContinue(
          this.characteristicDataService.getCharacteristicByTaskListHeaders(
            taskListGroup,
            masterGroupCounters
          ),
          (error) => {
            this.handleError(
              [
                MCode500Validation.name,
                TechnicalSpecificationSequenceValidation.name,
                Teamcenter2DMCodeValidation.name,
                MultiKVCode510Validation.name,
              ],
              error,
              'Unable to find characteristics'
            );
          }
        );
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

  private readonly otherCharacteristicsFromTechnicalSpecificationsForActiveEquipment$ =
    this.technicalSpecificationsForEquipment$.pipe(
      tap(() => {
        this.validationService.setValidationsToLoading(MCode500Validation.name);
      }),
      map((taskListHeaders) =>
        taskListHeaders.filter((taskListHeader) => !taskListHeader.description.includes('Master'))
      ),
      switchMap((masterTaskListHeaders) => {
        if (isEmpty(masterTaskListHeaders)) {
          return of([]);
        }

        const taskListGroup = masterTaskListHeaders[0].taskListGroup;
        const masterGroupCounters = masterTaskListHeaders.map(({ groupCounter }) => groupCounter);

        return catchAndContinue(
          this.characteristicDataService.getCharacteristicByTaskListHeaders(
            taskListGroup,
            masterGroupCounters
          ),
          (error) => {
            this.handleError(MCode500Validation.name, error, 'Unable to find characteristics');
          }
        );
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

  private readonly allCharacteristicsForActiveEquipment$ = combineLatest([
    this.masterCharacteristicsFromTechnicalSpecificationsForActiveEquipment$,
    this.otherCharacteristicsFromTechnicalSpecificationsForActiveEquipment$,
  ]).pipe(
    map(([masterCharacteristics, otherCharacteristics]) =>
      sortBy(
        masterCharacteristics.concat(otherCharacteristics),
        (characteristic) => characteristic.groupCounter
      )
    ),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly bomHeader$ = this.designId$.pipe(
    switchMap((designId) =>
      catchAndContinue(this.bomHeaderService.getSingle(designId), (error) => {
        this.handleError(
          [BomLockedValidation.name, MultiKVCode510Validation.name],
          error,
          'Could not load BOM'
        );
      })
    ),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly bomType$ = this.designId$.pipe(
    // We discard the designId since getMultiple will fetch the designId.
    switchMap((_) =>
      catchAndContinue(this.bomItemDataService.getMultiple(), (error) => {
        this.handleError([MultiKVCode510Validation.name], error, 'Could not load BOM type');
      })
    ),
    map(getBomType),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly milestones$ = this.designId$.pipe(
    switchMap((designId) => {
      const filters: IFilter = {
        key: taskDesignIdKey,
        operator: ODataOperator.Equals,
        value: designId,
      };

      return catchAndContinue(this.taskDataService.getMultiple(filters), (error) => {
        this.handleError(
          PartDesignAcceptedInTeamcenterValidation.name,
          error,
          'Could not load milestones'
        );
      });
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly isDuplo$ = this.designId$.pipe(
    switchMap((designId) =>
      this.materialDataService.getSingle(designId).pipe(
        map((material) => isDuploTopMainGroup(material.mainGroupTop)),
        // isDuplo is an input to three rules rather than a validation of its own. If material data
        // can't be loaded we degrade to non-duplo so those rules still run against the actual TS
        // data (surfacing their own errors as usual); we deliberately don't mark them FAILED here,
        // since the downstream setViolations would immediately overwrite that state anyway.
        // catchAndContinue is unsuitable: it emits EMPTY, which would stall the dependent
        // combineLatest subscriptions entirely.
        catchError(() => of(false))
      )
    ),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly modalService: NzModalService,
    private readonly designService: DesignService,
    private readonly equipmentLookup: EquipmentLookupService,
    private readonly validationService: ValidationService,
    private readonly specificationDataService: SpecificationDataService,
    private readonly characteristicDataService: CharacteristicDataService,
    private readonly legoRestApiDataService: LegoRestApiDataService,
    private readonly bomHeaderService: BomHeaderApiService,
    private readonly bomItemDataService: BOMItemDataService,
    private readonly taskDataService: TaskDataService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly oqcValidationService: OqcValidationService,
    private readonly materialDataService: MaterialDataService
  ) {}

  public ngOnInit(): void {
    // All technical specification validation subscription
    this.subscriptions.add(
      combineLatest([this.allTechnicalSpecifications$, this.isDuplo$]).subscribe(
        ([taskListHeaders, isDuplo]) => {
          const violations = TechnicalSpecificationFormatValidation.validate(
            taskListHeaders.data,
            isDuplo
          );

          this.validationService.setViolations(
            TechnicalSpecificationFormatValidation.name,
            violations
          );
        }
      )
    );

    // Master technical specification characteristics for active Equipment validation subscription
    this.subscriptions.add(
      this.masterCharacteristicsFromTechnicalSpecificationsForActiveEquipment$.subscribe(
        (masterCharacteristics) => {
          // Technical Specification Sequence Validation
          const violations =
            TechnicalSpecificationSequenceValidation.validate(masterCharacteristics);

          this.validationService.setViolations(
            TechnicalSpecificationSequenceValidation.name,
            violations
          );
        }
      )
    );

    this.subscriptions.add(
      combineLatest([this.allCharacteristicsForActiveEquipment$, this.isDuplo$]).subscribe(
        ([allCharacteristics, isDuplo]) => {
          const violations = MCode500Validation.validate(allCharacteristics, isDuplo);
          this.validationService.setViolations(MCode500Validation.name, violations);
        }
      )
    );

    this.subscriptions.add(
      combineLatest([this.milestones$, this.teamcenterPartDesignStructure$]).subscribe(
        ([_, partDesign]) => {
          const violations = PartDesignAcceptedInTeamcenterValidation.validate(partDesign);

          this.validationService.setViolations(
            PartDesignAcceptedInTeamcenterValidation.name,
            violations
          );
        }
      )
    );

    this.subscriptions.add(
      combineLatest([
        this.masterCharacteristicsFromTechnicalSpecificationsForActiveEquipment$,
        this.partDesign2DMCodes$,
      ]).subscribe(([masterCharacteristics, mCodeResult]) => {
        try {
          const violations = Teamcenter2DMCodeValidation.validate(
            masterCharacteristics,
            mCodeResult?.mCodes,
            mCodeResult?.revisionDescription
          );

          this.validationService.setViolations(Teamcenter2DMCodeValidation.name, violations);
        } catch (error) {
          if (!(error instanceof MCodeValidationNotPossibleError)) {
            throw error;
          }

          this.validationService.setErrorOnValidation(
            Teamcenter2DMCodeValidation.name,
            error.message,
            { ...error.diagnostics }
          );
        }
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.masterCharacteristicsFromTechnicalSpecificationsForActiveEquipment$,
        this.bomType$,
        this.isDuplo$,
      ]).subscribe(([masterCharacteristics, bomType, isDuplo]) => {
        const violations = MultiKVCode510Validation.validate(
          masterCharacteristics,
          bomType,
          isDuplo
        );

        this.validationService.setViolations(MultiKVCode510Validation.name, violations);
      })
    );

    this.subscriptions.add(
      this.bomHeader$.subscribe((bomHeader) => {
        const violations = BomLockedValidation.validate(bomHeader);

        this.validationService.setViolations(BomLockedValidation.name, violations);
      })
    );

    this.subscriptions.add(
      combineLatest([this.oqcValidationEnabled$, this.designId$])
        .pipe(
          filter(([enabled]) => enabled),
          map(([_, designId]) => designId),
          distinctUntilChanged(),
          switchMap((designId) =>
            catchAndContinue(this.oqcValidationService.validateOqc(designId), (error) => {
              this.handleError(OqcValidation.name, error, 'Unable to perform OQC validation');
            })
          )
        )
        .subscribe((validationResult) => {
          if (validationResult.validationFailed) {
            this.validationService.setErrorOnValidation(
              OqcValidation.name,
              validationResult.failureReason
            );

            return;
          }

          const violations = OqcValidation.validate(
            validationResult.errors,
            validationResult.oqcRevisionId
          );

          this.validationService.setViolations(OqcValidation.name, violations);
        })
    );
  }

  public openValidationModal() {
    const modal = this.modalService.create({
      nzClosable: true,
      nzContent: ValidationSummaryModalComponent,
      nzFooter: [
        {
          label: 'Close',
          onClick: () => {
            modal.destroy();
          },
        },
      ],
      nzMaskClosable: true,
      nzTitle: 'EMMET Validations',
      nzWidth: '70%',
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private handleError(validationIds: string[] | string, error: unknown, notFoundMessage: string) {
    if (!error) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      const errorMsg = error.status === 404 ? notFoundMessage : error.message;
      this.validationService.setErrorOnValidation(validationIds, errorMsg);

      return;
    }

    if (error instanceof SapError) {
      const errorMsg =
        error.httpStatusCode === 404 ? notFoundMessage : error.message.split('§§§')[0];

      this.validationService.setErrorOnValidation(validationIds, errorMsg);

      return;
    }

    if (error instanceof Error) {
      this.validationService.setErrorOnValidation(validationIds, error.message);

      return;
    }

    this.validationService.setErrorOnValidation(validationIds, String(error));
  }
}
